/* Dropshipping visibility sweeper — runs for EVERY visitor on every page
 * except the ds pages themselves (layout/theme.liquid skips emitting the
 * config there, and this script exits without it).
 *
 * Why this exists: dropshipping products are hidden server-side on every
 * theme-rendered surface (product-loop guard, sidebar, related, search
 * template, PDP 404 gate), but two families of surfaces render their own
 * product cards that theme Liquid cannot guard:
 *   - PageFly-generated sections (~170 pf-* files, regenerated wholesale on
 *     every PageFly republish — e.g. the /pages/latex-colors-by-brand-*
 *     pages), which server-render cards straight from regular automated
 *     collections that ds products match by vendor/title;
 *   - third-party frontends that inject cards client-side (the Searchanise
 *     dropdown and /search page, recommendation widgets).
 * This sweeper hides any element linking to a dropshipping product, wherever
 * it came from. A server-rendered <style> in theme.liquid (li:has(...) rules
 * built from the same handle list) already hides the common <li>-wrapped
 * cards before first paint; this script handles non-<li> structures and
 * content inserted after load.
 *
 * The handle list is authoritative — it is the dropshipping collection
 * itself (tag acc:dropshipping), rendered into window.DS_HIDDEN_HANDLES by
 * theme.liquid. Liquid caps collections[h].products at 50, so when the
 * collection outgrows that, the remainder is fetched from the
 * collection.handles view and cached in sessionStorage.
 */
(function () {
  'use strict';

  var handles = window.DS_HIDDEN_HANDLES;
  if (!handles || !handles.length) return; // ds pages, or nothing to hide

  var currentHandle = window.DS_SWEEP_CURRENT || ''; // eligible ds PDP: never
  // hide elements pointing at the product being viewed (breadcrumbs etc.)
  var hidden = new Set();
  handles.forEach(function (h) {
    if (h && h !== currentHandle) hidden.add(h);
  });

  var PRODUCT_PATH = /\/products\/([^/?#]+)\/?$/;

  function handleFromPath(path) {
    if (!path) return null;
    var m = PRODUCT_PATH.exec(path.split('?')[0].split('#')[0]);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch (e) {
      return m[1];
    }
  }

  function handleFromEl(el) {
    // <a href> resolves relative URLs for us; data-href (PageFly image/title
    // pseudo-links) is a raw path string.
    return handleFromPath(el.pathname || el.getAttribute('data-href'));
  }

  // Nearest ancestor that reads as a single product card. PageFly boxes come
  // first — their ViewDetails <a> carries data-product-id itself, so a bare
  // closest('[data-product-id]') would match the anchor and hide too little.
  // If the candidate also references products we are NOT hiding (e.g. a menu
  // branch matched via li), fall back to hiding just the link element.
  var CARD_SELECTOR = '[data-pf-type="ProductBox"], .snize-product, li, .grid__item, .product-card';

  function containerFor(el) {
    var card = el.closest(CARD_SELECTOR);
    if (!card && el.parentElement) card = el.parentElement.closest('[data-product-id]');
    if (!card) return el;
    var links = card.querySelectorAll('a[href*="/products/"], [data-href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var h = handleFromEl(links[i]);
      if (h && !hidden.has(h)) return el;
    }
    return card;
  }

  function hideCard(card) {
    card.style.setProperty('display', 'none', 'important');
    // PageFly grids: each card sits alone in a div.pf-c column; hide the
    // column too or the row keeps a card-sized hole.
    var col = card.parentElement;
    if (col && col.classList.contains('pf-c') && col.children.length === 1) {
      col.style.setProperty('display', 'none', 'important');
    }
  }

  function sweep(root) {
    var links = (root || document).querySelectorAll('a[href*="/products/"], [data-href*="/products/"]');
    links.forEach(function (el) {
      if (el.dataset.dsSwept) return;
      el.dataset.dsSwept = '1';
      var h = handleFromEl(el);
      if (!h || !hidden.has(h)) return;
      // Real cart page (#cartform, action exactly "/cart"): an accidentally-
      // added ds line must stay visible — it carries a warning and the
      // customer needs the remove control. theme.liquid skips the CSS layer
      // there for the same reason. Must NOT prefix-match: PageFly wraps each
      // whole card in a form[action="/cart/add"].
      if (el.closest('form[action="/cart"]')) return;
      hideCard(containerFor(el));
    });
  }

  // ---- >50 products: complete the list from the collection.handles view ----
  var total = window.DS_HIDDEN_TOTAL || 0;
  var collectionHandle = window.DS_SWEEP_COLLECTION;
  var CACHE_KEY = 'ds_sweep_handles_v1';
  var CACHE_TTL = 3600e3;

  function addAll(list) {
    if (!Array.isArray(list)) return;
    var grew = false;
    list.forEach(function (h) {
      if (h && h !== currentHandle && !hidden.has(h)) {
        hidden.add(h);
        grew = true;
      }
    });
    if (grew) {
      // New handles invalidate earlier per-element verdicts (anchors AND
      // data-href pseudo-links).
      document.querySelectorAll('[data-ds-swept]').forEach(function (el) {
        delete el.dataset.dsSwept;
      });
      sweep();
    }
  }

  function completeHandleList() {
    if (!collectionHandle || total <= handles.length) return;
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < CACHE_TTL) {
        addAll(cached.h);
        return;
      }
    } catch (e) { /* fall through to fetch */ }

    var pages = Math.min(Math.ceil(total / 250), 8);
    var requests = [];
    for (var p = 1; p <= pages; p++) {
      requests.push(
        fetch('/collections/' + encodeURIComponent(collectionHandle) + '?view=handles&page=' + p)
          .then(function (r) { return r.ok ? r.text() : '[]'; })
          .then(function (t) { return JSON.parse(t); })
          .catch(function () { return []; })
      );
    }
    Promise.all(requests).then(function (chunks) {
      var all = [].concat.apply([], chunks);
      if (!all.length) return;
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), h: all }));
      } catch (e) { /* storage full/blocked — still sweep this page */ }
      addAll(all);
    });
  }

  // ---- run ----
  function start() {
    sweep();
    completeHandleList();
    // Late-inserted content: Searchanise dropdown / search results, infinite
    // scroll, recommendation apps. childList-only, so our own style/attribute
    // writes never retrigger it.
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        sweep();
      }, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
